import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import moment from 'moment';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { IMedicationDetailed } from '../interfaces/Medication/IMedicationDetailed';
import { IAddMedication } from '../interfaces/Medication/IAddMedication';
import { IScheduledDays } from '../interfaces/Medication/IScheduledDays';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';
import { date_format, datetime_format } from 'src/date/date.configuration';

const SCHEDULED_DAY_LABELS: Array<{ key: keyof IScheduledDays; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

@Injectable({ providedIn: 'root' })
export class PastMedicationService {
  public pastMedicationDataSub = new BehaviorSubject<IMedicationDetailed[]>([]);

  public pastMedicationLoadingSub = new BehaviorSubject<boolean>(false);
  public pastMedicationLoadingSub$ =
    this.pastMedicationLoadingSub.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  loadData(): Observable<IGenericServiceResponse<IMedicationDetailed[]>> {
    this.pastMedicationLoadingSub.next(true);
    return this.getPastMedication(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId()
    ).pipe(
      tap((response) => {
        this.pastMedicationDataSub.next(
          response?.data?.map((item) => this.enrichMedicationForTable(item))!
        );
      }),
      catchError((err) => {
        this.swalToastService.toastError('Unable to retrieve past medications');
        console.error('Error fetching past medications:', err);
        return EMPTY;
      }),
      finalize(() => this.pastMedicationLoadingSub.next(false)),
    );
  }

  getData(): void {
    this.loadData().subscribe();
  }

  getPastMedication(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<IMedicationDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (visitId) {
      return this._http.get<IGenericServiceResponse<IMedicationDetailed[]>>(
        `${environment.BACKEND_URL}Medication/PastMedications/${patientId}?visitId=${visitId}`,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.get<IGenericServiceResponse<IMedicationDetailed[]>>(
        `${environment.BACKEND_URL}Medication/PastMedications/${patientId}`,
        {
          headers: headers,
        }
      );
    }
  }

  editPastMedication(pastMedicationsObj: IAddMedication) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}Medication/${pastMedicationsObj.id}`,
      pastMedicationsObj,
      {
        headers: headers,
      }
    );
  }

  deletePastMedication(pastMedicationId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Medication/${pastMedicationId}`,
      {
        headers: headers,
      }
    );
  }

  deletePastMedicationFromTable(medicationId: string) {
    // REMOVE THE Medical History
    this.pastMedicationDataSub.next(
      this.pastMedicationDataSub.value?.filter(
        (pm) => pm.id?.toString() != medicationId
      )
    );
  }

  // Builds the display-only fields the medication table needs (period range,
  // scheduled days summary, product name truncated via CSS when it overflows
  // its column) since the backend only sends the raw data these are derived
  // from.
  private enrichMedicationForTable(item: IMedicationDetailed): IMedicationDetailed {
    const result: IMedicationDetailed & { [key: string]: any } = {
      ...item,
      periodCombination: this.getMedicationPeriodLabel(
        item?.onSetDateTime,
        item?.endDateTime
      ),
      scheduledDaysCombination: this.getScheduledDaysLabel(
        item?.scheduledDays
      ),
    };

    if (item?.productCombination) {
      result['__badge__productCombination'] = {
        text: item.productCombination,
        className: 'truncate-cell',
        title: item.productCombination,
      };
    }

    return result;
  }

  private formatMedicationDate(value?: string): string | null {
    if (!value) {
      return null;
    }

    const parsed = moment(
      value,
      [moment.ISO_8601, datetime_format, date_format],
      true
    );
    return parsed.isValid() ? parsed.format(date_format) : value;
  }

  private getMedicationPeriodLabel(
    onSetDateTime?: string,
    endDateTime?: string
  ): string {
    const start = this.formatMedicationDate(onSetDateTime);

    if (!start) {
      return 'N/A';
    }

    const end = this.formatMedicationDate(endDateTime) ?? 'Present';

    return `From ${start} to ${end}`;
  }

  private getScheduledDaysLabel(scheduledDays?: IScheduledDays | null): string {
    const selectedDays = scheduledDays
      ? SCHEDULED_DAY_LABELS.filter((day) => scheduledDays[day.key])
      : [];

    if (selectedDays.length === SCHEDULED_DAY_LABELS.length) {
      return 'Everyday';
    }

    if (selectedDays.length === 0) {
      return 'N/A';
    }

    return selectedDays.map((day) => day.label).join(', ');
  }
}
